import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';

const GRID_COLUMNS = '1.1fr 1.5fr 1fr 0.8fr 1fr 1.2fr';

const VERDICT_CHIP = {
  clean: { type: 'successNeutral', label: 'Clean' },
  pii: { type: 'warningNeutral', label: 'PII' },
  prompt_injection: { type: 'errorNeutral', label: 'Prompt injection' },
  both: { type: 'errorEmphasis', label: 'Multiple threats' },
  processing: { type: 'staticSubtle', label: 'Processing' },
};

const STATUS_CHIP = {
  completed: { type: 'successNeutral', label: 'Completed' },
  failed: { type: 'errorNeutral', label: 'Failed' },
  partial: { type: 'warningNeutral', label: 'Partial' },
  processing: { type: 'staticNeutral', label: 'Processing' },
};

const formatRelativeTime = (iso) => {
  const then = new Date(iso);
  const now = new Date();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const HeaderCell = ({ children }) => (
  <Typography variant="captionSubtle">{children}</Typography>
);

const RecentScansTable = () => {
  const tokens = useTokens();
  const history = useHistory();
  const recentScans = useSelector(
    (s) => s.guardian.dashboard.recentScans || []
  );
  const loading = useSelector((s) => s.guardian.dashboard.loading);

  const rows = recentScans.slice(0, 10);

  return (
    <Card
      bdsType="staticSubtle"
      bdsBoxShadow="default"
      bdsBorderRadius="round"
      bdsPadding="spacious"
    >
      <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
          <Typography variant="h4">Recent scans</Typography>
          <Typography variant="captionSubtle">
            Latest gateway traffic, newest first
          </Typography>
        </Stack>

        {loading && rows.length === 0 ? (
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={36} />
            ))}
          </Stack>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              padding: tokens.aliasesSpaceContainerDefault,
              textAlign: 'center',
            }}
          >
            <Typography variant="captionSubtle">No scans yet.</Typography>
          </Box>
        ) : (
          <Stack sx={{ gap: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                gap: tokens.aliasesSpaceComponentDefault,
                paddingY: tokens.aliasesSpaceComponentDense,
                paddingX: tokens.aliasesSpaceComponentDefault,
                borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
              }}
            >
              <HeaderCell>Time</HeaderCell>
              <HeaderCell>Content type</HeaderCell>
              <HeaderCell>Region</HeaderCell>
              <HeaderCell>Duration</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Verdict</HeaderCell>
            </Box>
            {rows.map((scan) => {
              const verdict = VERDICT_CHIP[scan.verdict] || {
                type: 'staticNeutral',
                label: scan.verdict || 'Unknown',
              };
              const status = STATUS_CHIP[scan.status] || {
                type: 'staticNeutral',
                label: scan.status || 'Unknown',
              };
              return (
                <Box
                  key={scan.id}
                  onClick={() =>
                    history.push(`/scans/${scan.correlation_id}`)
                  }
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLUMNS,
                    gap: tokens.aliasesSpaceComponentDefault,
                    paddingY: tokens.aliasesSpaceComponentDefault,
                    paddingX: tokens.aliasesSpaceComponentDefault,
                    borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                    cursor: 'pointer',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor:
                        tokens.aliasesColorBgInteractiveContainerSubtleDefault,
                    },
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography variant="body1">
                    {formatRelativeTime(scan.started_at)}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {scan.content_type}
                  </Typography>
                  <Typography variant="body1">
                    {scan.region || '—'}
                  </Typography>
                  <Typography variant="body1">
                    {scan.duration_ms ? `${scan.duration_ms} ms` : '—'}
                  </Typography>
                  <Box>
                    <Chip
                      bdsType={status.type}
                      bdsSize="medium"
                      label={status.label}
                    />
                  </Box>
                  <Box>
                    <Chip
                      bdsType={verdict.type}
                      bdsSize="medium"
                      label={verdict.label}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default RecentScansTable;
